import { Reducer, AnyAction, Action } from 'redux';
import { EditableListState } from './types-store';
import {
  ACTION, ClearList,
  RequestMetadata, ReceiveMetadata, FailMetadata,
  RequestList, ReceiveList, FailList,
  ModifyItem, CancelChanges,
  RequestSaveChanges, ReceiveSaveChanges, FailSaveChanges,
  ValidationChange,
  RequestExportExcel, ReceiveExportExcel, FailExportExcel,
  OpenExcelImport, CloseExcelImport,
  RequestImportExcel, ReceiveImportExcel, FailImportExcel, ClearImportValidationErrors
} from './action-types';
import { ListType } from './constants';
import {
  CompareValueFunction, FindModifiedItemFunction, GetModifiedItemFunction,
  GetPersistedItemFunction, RemoveValidationFunction, UpdatePersistedValueFunction
} from './types';

export const editableListInitialStateFactory = <METADATA, ITEM_DATA, MODIFIED_DATA, SEARCH_MODEL, VALIDATION_ERROR>(
): EditableListState<METADATA, ITEM_DATA, MODIFIED_DATA, SEARCH_MODEL, VALIDATION_ERROR> => {
  return {
    fetchingMetadata: false,
    metadata: undefined,
    requestId: 0,
    fetching: false,
    updating: false,
    requestedSearchModel: undefined,
    numberOfResults: undefined,
    listItems: undefined,
    modifiedItems: [],
    validation: {},
    exceededLengthError: undefined,
    exportExcelFetching: false,
    showExcelImport: false,
    importExcelFetching: false,
    importValidationErrors: undefined
  };
};

// eslint:disable-next-line: max-func-body-length
export const editableListReducerFactory = <METADATA, ITEM_DATA, MODIFIED_DATA, SEARCH_MODEL, VALIDATION_ERROR>(
  listType: ListType,
  initialState: EditableListState<METADATA, ITEM_DATA, MODIFIED_DATA, SEARCH_MODEL, VALIDATION_ERROR>,
  getModifiedItem: GetModifiedItemFunction<ITEM_DATA, MODIFIED_DATA>,
  findModifiedItem: FindModifiedItemFunction<MODIFIED_DATA>,
  getPersistedItem: GetPersistedItemFunction<ITEM_DATA, MODIFIED_DATA>,
  compareValue: CompareValueFunction<ITEM_DATA, MODIFIED_DATA>,
  updatePersistedValue: UpdatePersistedValueFunction<ITEM_DATA, MODIFIED_DATA>,
  removeValidation: RemoveValidationFunction<MODIFIED_DATA>
): Reducer<EditableListState<METADATA, ITEM_DATA, MODIFIED_DATA, SEARCH_MODEL, VALIDATION_ERROR>> => {

  type BranchState = EditableListState<METADATA, ITEM_DATA, MODIFIED_DATA, SEARCH_MODEL, VALIDATION_ERROR>;

  const reducers: { [indexer: string]: Reducer<BranchState, Action> } = {

    [ACTION.REQUEST_METADATA]: (state: BranchState, action: RequestMetadata): BranchState => {

      return {
        ...state,
        fetchingMetadata: true
      };
    },

    [ACTION.RECEIVE_METADATA]: (state: BranchState, action: ReceiveMetadata<METADATA>): BranchState => {

      return {
        ...state,
        metadata: action.metadata,
        fetchingMetadata: false
      };
    },

    [ACTION.FAIL_METADATA]: (state: BranchState, action: FailMetadata): BranchState => {

      return {
        ...state,
        fetchingMetadata: false
      };
    },

    [ACTION.CLEAR_LIST]: (
      state: BranchState,
      action: ClearList
    ): BranchState => {
      return {
        ...state,
        requestId: action.requestId,
        fetching: false,
        requestedSearchModel: undefined,
        numberOfResults: undefined,
        listItems: undefined,
        modifiedItems: [],
        validation: {},
        exceededLengthError: undefined
      };
    },

    [ACTION.REQUEST_LIST]: (state: BranchState, action: RequestList<SEARCH_MODEL>): BranchState => {

      return {
        ...state,
        requestId: action.requestId,
        fetching: true,
        requestedSearchModel: action.model,
        exceededLengthError: undefined,
        ...(action.clearResults && {
          numberOfResults: undefined,
          listItems: undefined,
          modifiedItems: []
        })
      };
    },

    [ACTION.RECEIVE_LIST]: (state: BranchState, action: ReceiveList<ITEM_DATA>): BranchState => {

      if (action.requestId !== state.requestId) {
        return state;
      }

      return {
        ...state,
        fetching: false,
        numberOfResults: action.numberOfResults,
        listItems: [
          ...(state.listItems || []),
          ...action.list
        ]
      };
    },

    [ACTION.FAIL_LIST]: (state: BranchState, action: FailList): BranchState => {

      if (action.requestId !== state.requestId) {
        return state;
      }

      return {
        ...state,
        fetching: false,
        exceededLengthError: action.errors
      };
    },

    [ACTION.MODIFY_ITEM]: (state: BranchState, action: ModifyItem<MODIFIED_DATA>): BranchState => {

      const modifiedData = action.modifiedData;
      const persistedText = getPersistedItem(state.listItems, modifiedData);

      if (!persistedText) {
        return state;
      }

      const previousModifiedItem = findModifiedItem(state.modifiedItems, modifiedData);

      const validation = removeValidation(state.validation, modifiedData, previousModifiedItem);

      if (compareValue(persistedText, modifiedData)) {
        return {
          ...state,
          modifiedItems: (state.modifiedItems || []).filter(item =>
            item !== previousModifiedItem
          ),
          validation
        };
      }

      return {
        ...state,
        modifiedItems: [
          ...(state.modifiedItems || []).filter(item =>
            item !== previousModifiedItem
          ),
          modifiedData
        ],
        validation
      };
    },

    [ACTION.CANCEL_CHANGES]: (state: BranchState, action: CancelChanges): BranchState => {

      return {
        ...state,
        modifiedItems: [],
        validation: {}
      };
    },

    [ACTION.REQUEST_SAVE_CHANGES]: (state: BranchState, action: RequestSaveChanges): BranchState => {

      return {
        ...state,
        updating: true
      };
    },

    [ACTION.RECEIVE_SAVE_CHANGES]: (state: BranchState, action: ReceiveSaveChanges): BranchState => {

      return {
        ...state,
        listItems: (state.listItems || []).map(persistedText => {
          const modifiedText = getModifiedItem(state.modifiedItems || [], persistedText);

          return updatePersistedValue(persistedText, modifiedText);
        }),
        modifiedItems: [],
        updating: false
      };
    },

    [ACTION.FAIL_SAVE_CHANGES]: (state: BranchState, action: FailSaveChanges): BranchState => {

      return {
        ...state,
        updating: false
      };
    },

    [ACTION.VALIDATION_CHANGE]: (state: BranchState, action: ValidationChange): BranchState => {

      return {
        ...state,
        validation: action.validation
      };
    },

    [ACTION.REQUEST_EXPORT_EXCEL]: (state: BranchState, action: RequestExportExcel): BranchState => {

      return {
        ...state,
        exportExcelFetching: true
      };
    },

    [ACTION.RECEIVE_EXPORT_EXCEL]: (state: BranchState, action: ReceiveExportExcel): BranchState => {

      return {
        ...state,
        exportExcelFetching: false
      };
    },

    [ACTION.FAIL_EXPORT_EXCEL]: (state: BranchState, action: FailExportExcel): BranchState => {

      return {
        ...state,
        exportExcelFetching: false
      };
    },

    [ACTION.OPEN_EXCEL_IMPORT]: (state: BranchState, action: OpenExcelImport): BranchState => {

      return {
        ...state,
        showExcelImport: true
      };
    },

    [ACTION.CLOSE_EXCEL_IMPORT]: (state: BranchState, action: CloseExcelImport): BranchState => {

      return {
        ...state,
        showExcelImport: false
      };
    },

    [ACTION.REQUEST_IMPORT_EXCEL]: (state: BranchState, action: RequestImportExcel): BranchState => {

      return {
        ...state,
        importExcelFetching: true
      };
    },

    [ACTION.RECEIVE_IMPORT_EXCEL]: (state: BranchState, action: ReceiveImportExcel): BranchState => {

      return {
        ...state,
        importExcelFetching: false,
        showExcelImport: false
      };
    },

    [ACTION.FAIL_IMPORT_EXCEL]: (state: BranchState, action: FailImportExcel<VALIDATION_ERROR>): BranchState => {

      return {
        ...state,
        importExcelFetching: false,
        importValidationErrors: action.importValidationErrors
      };
    },

    [ACTION.CLEAR_IMPORT_VALIDATION_ERRORS]: (state: BranchState, action: ClearImportValidationErrors): BranchState => {

      return {
        ...state,
        importValidationErrors: undefined
      };
    }
  };

  return (state: BranchState = initialState, action: AnyAction & { listType: ListType }): BranchState => {
    if (action.listType !== listType) {
      return state;
    }

    const r = reducers[action.type];
    return (r && r(state, action)) || state;
  };
};
